import { useEffect, useRef, useState } from 'react';

/**
 * Animates a numeric value from its previous render to a new target over
 * `duration`ms with an ease-out-cubic curve, re-rendering on every frame —
 * this is what makes the Security Summary Assessment Results charts sweep to their
 * target instead of snapping, ported from the requestAnimationFrame driver
 * in dummy_webpage/current/view2.js's animateNumber().
 *
 * Respects the user's OS-level reduced-motion preference by jumping
 * straight to the target instead of animating.
 */
export function useAnimatedNumber(target: number, duration = 800): number {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setValue(target);
      fromRef.current = target;
      return undefined;
    }

    const from = fromRef.current;
    const delta = target - from;
    const start = performance.now();

    function tick(now: number) {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = from + delta * eased;
      setValue(current);

      if (t < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
      }
    }

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration]);

  return value;
}
